import { GenericGridSmartViewsController } from '../controller/GenericGridSmartViewsController';
import { BaseWebComponentElement } from '../../../framework/BaseWebComponentElement';
import {
  CategoriesConfig,
  InitialView,
  SmartViewItemConfiguration,
  SmartViewsCreateRequest,
  SmartViewsRecentCreateRequest,
  SmartViewsUpdateRequest,
  UpdateSmartViewSettingsRequest,
} from '../types';

export class GenericGridSmartViewsWebComponent extends BaseWebComponentElement {
  private _categoriesConfig: CategoriesConfig;
  private _hasSearchInput: boolean;
  public emptySavedViewsText: string;
  public searchPlaceholderString: string;
  public controller: GenericGridSmartViewsController | null;
  private _initialView: InitialView;

  public customErrorMappingFn: (errorCode: number) => string | null;
  public isEditedFn: () => Promise<boolean>;
  public searchFn: ((string, SmartViewItemConfiguration) => boolean) | null = null;
  public decoratedTextOnSearchFn: ((string, SmartViewItemConfiguration) => string) | null = null;

  constructor() {
    super();
    this.initialSetup({
      controllerConstructor: GenericGridSmartViewsController,
    });
  }

  set categoriesConfig(categoriesConfig: CategoriesConfig) {
    this._categoriesConfig = categoriesConfig;
  }
  /**
   *
   * contains customCategories and internalCategories
   * customCategories -> external categories received as a prop
   * internalCategories -> component handles the configuration for these categories, but it's necessary to specify which are present
   */
  get categoriesConfig(): CategoriesConfig {
    return this._categoriesConfig ?? {};
  }

  /**
   * if set to true, the search input will be displayed
   */
  set hasSearchInput(hasSearchInput: boolean) {
    this._hasSearchInput = hasSearchInput;
  }

  get hasSearchInput(): boolean {
    return this._hasSearchInput ?? false;
  }

  saveSmartView(view: SmartViewsCreateRequest, viewCreationConfig) {
    this.controller?.saveSmartView(view, viewCreationConfig);
  }

  async saveSmartViewDirectly(viewCreationConfig: SmartViewsCreateRequest): Promise<SmartViewItemConfiguration | null> {
    if (!this.controller) {
      return null;
    }

    return this.controller?.createNewView(viewCreationConfig, true);
  }

  async updateSmartView(view: SmartViewsUpdateRequest): Promise<SmartViewItemConfiguration | null> {
    if (!this.controller) {
      return null;
    }
    return this.controller?.handleUpdateSmartView(view);
  }

  async updateRecentView(view: SmartViewsUpdateRequest): Promise<SmartViewItemConfiguration | null> {
    if (!this.controller) {
      return null;
    }
    return this.controller?.handleUpdateSmartView(view);
  }

  makeSmartViewFavorite(viewId: string) {
    this.controller?.makeSmartViewFavorite(viewId);
  }

  removeFromFavorite(viewId: string) {
    this.controller?.removeFromFavorite(viewId);
  }

  getActiveView(): SmartViewItemConfiguration {
    return this.controller?.getActiveView() as SmartViewItemConfiguration;
  }

  setActiveViewIdInState(viewId: string) {
    this.controller?.updateActiveViewId(viewId);
  }

  setActiveViewAndSelect(viewId: string) {
    this.controller?.setActiveViewAndSelect(viewId);
  }

  updateSmartViewsSettings(settings: UpdateSmartViewSettingsRequest) {
    return this.controller?.updateSmartViewsSettings(settings);
  }

  addRecentView(view: SmartViewsRecentCreateRequest, select = false) {
    return this.controller?.addRecentView(view, select);
  }

  set initialView(initialView: InitialView) {
    this._initialView = initialView;
  }

  get initialView() {
    return this._initialView;
  }

  async reloadCategories(focusOnSelectedView: boolean = false) {
    await this.controller?.reloadCategories(focusOnSelectedView);
  }

  get smartViewState() {
    return this.controller?.getViewState();
  }
}
